import './LatestPage.css';
import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore
import { enableScroll } from './Scroll';
// @ts-ignore
import { Helmet } from 'react-helmet';
// @ts-ignore
import { FetchCountryName } from './Utils/CountryUtility';
// @ts-ignore
import { HandleDuplicateSightseeingPosts } from './Sightseeing/SightseeingLayout';
import { HandleDuplicateFoodPosts } from './Food/FoodLayout';
import { applyImageFallback, getDynamicPictureUrl } from './Utils/ImageFallback';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const canonicalUrl = 'https://livingoutofabackpack.com/latestposts/';

const LatestPage = () => {
  const [sightseeingData, setSightseeingData] = useState<SightseeingPost[]>([]);
  const [foodData, setFoodData] = useState<FoodPost[]>([]);
  const [sightseeingPage, setSightseeingPage] = useState(1);
  const [foodPage, setFoodPage] = useState(1);
  const [totalSightseeingPages, setTotalSightseeingPages] = useState(1);
  const [totalFoodPages, setTotalFoodPages] = useState(1);
  const [ItemLoadingFood, setItemLoadingFood] = useState(true);
  const [ItemLoadingSightseeing, setItemLoadingSightseeing] = useState(true);
  const [openPanel, setOpenPanel] = useState<number | null>(null);

  const arrayContainerRef = useRef<HTMLDivElement | null>(null);
  const toggleButtonRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sightseeing');

  const latestSightseeingImage = sightseeingData[0]?.images?.[0];
  const latestFoodImage = foodData[0]?.images?.[0];
  const socialImage = activeTab === 'sightseeing'
    ? (latestSightseeingImage ? `${backendUrl}/django-dynamic/pictures/${latestSightseeingImage}` : 'https://livingoutofabackpack.com/static/logo.jpg')
    : (latestFoodImage ? `${backendUrl}/django-dynamic/pictures/${latestFoodImage}` : 'https://livingoutofabackpack.com/static/logo.jpg');

  const isSightseeingPost = (post: SightseeingPost | FoodPost): post is SightseeingPost =>
    'place' in post || 'activity' in post;

  const getPostName = (post: SightseeingPost | FoodPost, index: number): string => {
    if (isSightseeingPost(post)) {
      return post.place || post.activity || `Travel post ${index + 1}`;
    }

    return post.dish || post.restaurant || `Travel post ${index + 1}`;
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://livingoutofabackpack.com/home/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Latest Posts',
        item: canonicalUrl,
      },
    ],
  };

  const latestPostsJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Latest Travel Posts & Updates',
    url: canonicalUrl,
    description: 'Recent sightseeing and food travel posts from around the world.',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: (activeTab === 'sightseeing' ? sightseeingData : foodData).slice(0, 10).map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: canonicalUrl,
        name: getPostName(post, index),
      })),
    },
  };

  interface CountryEntry {
    iso: string;
    id: number;
    created_at?: string;
    updated_at?: string;
  }

  interface BasePost {
    representative_id: number;
    countries: CountryEntry[];
    coordinates?: string;
    created_at?: string;
    updated_at?: string;
    images: string[];
    iso: string;
    travel_category: string;
  }

  interface SightseeingPost extends BasePost {
    place?: string;
    activity?: string;
  }

  interface FoodPost extends BasePost {
    dish?: string;
    restaurant?: string;
  }

  const handleNavigate = async (post: BasePost, type: 'sightseeing' | 'food') => {
    try {
      const countryName = await FetchCountryName(post.iso);

      // Fetch cursor instead of page
      const cursorResponse = await axios.get(`${backendUrl}/worlddata/${type}/get-cursor-for-item/${post.representative_id}/9/?iso=${post.iso}`);

      const cursor = cursorResponse.data.cursor;
      const cursorToken = type === 'sightseeing' || type === 'food' ? (cursorResponse.data.cursor_token || '') : '';

      navigate(`/traveltopics/${countryName}/topics/${type}`, {
        state: {
          selectedItemId: post.representative_id,
          selectedItemCursor: cursor,
          ...(type === 'sightseeing' || type === 'food' ? { selectedItemCursorToken: cursorToken } : {}),
          category: '', //post.travel_category ?? ""
        },
      });
    } catch (error) {
      console.error(`Error navigating to ${type} item from latest page:`, error);
    }
  };

  const fetchLatestSightseeing = (page = 1) => {
    setItemLoadingSightseeing(true);
    axios
      .get(`${backendUrl}/worlddata/sightseeing/get-sightseeing-data-linked/9/?page=${page}`)
      .then((response) => {
        setSightseeingData(response.data.results);
        setTotalSightseeingPages(response.data.total_pages);
        setItemLoadingSightseeing(false);
      })
      .catch((error) => console.error('Error fetching latest posts', error));
  };

  const fetchLatestFood = (page = 1) => {
    setItemLoadingFood(true);
    axios
      .get(`${backendUrl}/worlddata/food/get-food-data-linked/9/?page=${page}`)
      .then((response) => {
        setFoodData(response.data.results);
        setTotalFoodPages(response.data.total_pages);
        setItemLoadingFood(false);
      })
      .catch((error) => console.error('Error fetching latest posts', error));
  };

  useEffect(() => {
    const arrayContainer = arrayContainerRef.current;
    const cleanupScroll = enableScroll(arrayContainer);
    return cleanupScroll;
  }, [activeTab]);

  const switchTab = (tab: 'sightseeing' | 'food') => {
    setOpenPanel(null);
    setActiveTab(tab);
  };

  useEffect(() => {
    fetchLatestSightseeing();
    fetchLatestFood();
  }, []);

  useEffect(() => {
    const handleClickDuplicate = (event: MouseEvent) => {
      if (toggleButtonRef.current && !toggleButtonRef.current.contains(event.target as Node)) {
        setOpenPanel(null);
      }
    };

    document.addEventListener('click', handleClickDuplicate);
    return () => {
      document.removeEventListener('click', handleClickDuplicate);
    };
  }, []);

  return (
    <div className="LatestPage">
      <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8141845068352110" crossOrigin="anonymous"></script>
      <Helmet>
        <title>Latest Travel Posts & Updates | Living Out Of A Backpack</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="description" content="Read the latest travel posts, newly added sightseeing spots, and food discoveries from destinations around the world." />
        <meta name="keywords" content="travel blog updates, latest travel posts, new sightseeing spots, food discoveries" />
        <meta property="og:title" content="Latest Travel Posts & Updates | Living Out Of A Backpack" />
        <meta property="og:description" content="See recently added travel highlights, including sightseeing and food posts from multiple countries." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={socialImage} />
        <meta property="og:image:alt" content={`Latest ${activeTab} travel post highlights`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(latestPostsJsonLd)}</script>
      </Helmet>

      {/* Flip Tab */}
      <div className="LatestPosts">
        <div className="FlipTab">
          <div className="CategoryTab">
            <button className={`tab ${activeTab === 'sightseeing' ? 'active' : ''}`} onClick={() => switchTab('sightseeing')}>
              Sightseeing
            </button>
            <button className={`tab ${activeTab === 'food' ? 'active' : ''}`} onClick={() => switchTab('food')}>
              Food
            </button>
          </div>

          {activeTab === 'sightseeing' ? (
            <div className="Pagination">
              <button
                disabled={sightseeingPage === 1}
                onClick={() => {
                  const newPage = sightseeingPage - 1;
                  setSightseeingPage(newPage);
                  fetchLatestSightseeing(newPage);
                }}
              >
                <span className="material-icons">chevron_left</span>
              </button>

              <span className="PageIndicator">
                <div className="Title">Recent posts</div>
                Page {sightseeingPage} of {totalSightseeingPages}
              </span>

              <button
                disabled={sightseeingPage === totalSightseeingPages}
                onClick={() => {
                  const newPage = sightseeingPage + 1;
                  setSightseeingPage(newPage);
                  fetchLatestSightseeing(newPage);
                }}
              >
                <span className="material-icons">chevron_right</span>
              </button>
            </div>
          ) : (
            <div className="Pagination">
              <button
                disabled={foodPage === 1}
                onClick={() => {
                  const newPage = foodPage - 1;
                  setFoodPage(newPage);
                  fetchLatestFood(newPage);
                }}
              >
                <span className="material-icons">chevron_left</span>
              </button>

              <span className="PageIndicator">
                <div className="Title">Recent posts</div>
                Page {foodPage} of {totalFoodPages}
              </span>

              <button
                disabled={foodPage === totalFoodPages}
                onClick={() => {
                  const newPage = foodPage + 1;
                  setFoodPage(newPage);
                  fetchLatestFood(newPage);
                }}
              >
                <span className="material-icons">chevron_right</span>
              </button>
            </div>
          )}
        </div>

        <div className="LatestPostsContainer">
          {activeTab === 'sightseeing' ? (
            <div className={`LatestPostsArray`} ref={arrayContainerRef}>
              {ItemLoadingSightseeing ? (
                <div className="LatestListStatus">
                  <div className="loader" />
                </div>
              ) : sightseeingData.length === 0 ? (
                <div className="LatestListStatus">No sightseeing posts available.</div>
              ) : (
                sightseeingData.map((post) => (
                  <div key={post.representative_id} className={`array-item ${openPanel === post.representative_id ? 'panel-open' : ''}`}>
                    <div className="actual-item" onClick={() => handleNavigate(post, 'sightseeing')}>
                      <div className="picture-content">
                        <img
                          src={getDynamicPictureUrl(post.images.at(0))}
                          onError={applyImageFallback}
                          title={post.place || post.activity}
                          alt={post.place || post.activity}
                        />
                      </div>
                      <div className="block-title">{post.place || post.activity}</div>
                      {post.coordinates ? <div className="coordinates">{post.coordinates}</div> : null}
                      <div className="TimeStamp">
                        <div className="TimeStampUpdated">Updated: {(post.updated_at || post.countries?.[0]?.updated_at)?.replace(/[TZ]/g, ' ')?.replace(/\.\d{3}/, '') || 'Date unavailable'}</div>
                      </div>
                    </div>
                    <HandleDuplicateSightseeingPosts post={post} toggleButtonRef={toggleButtonRef} openPanel={openPanel} setOpenPanel={setOpenPanel} />
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className={`LatestPostsArray`} ref={arrayContainerRef}>
              {ItemLoadingFood ? (
                <div className="LatestListStatus">
                  <div className="loader" />
                </div>
              ) : foodData.length === 0 ? (
                <div className="LatestListStatus">No food posts available.</div>
              ) : (
                foodData.map((post) => (
                  <div key={post.representative_id} className={`array-item ${openPanel === post.representative_id ? 'panel-open' : ''}`}>
                    <div className="actual-item" onClick={() => handleNavigate(post, 'food')}>
                      <div className="picture-content">
                        <img
                          src={getDynamicPictureUrl(post.images.at(0))}
                          onError={applyImageFallback}
                          title={post.dish || post.restaurant}
                          alt={post.dish || post.restaurant}
                        />
                      </div>
                      <div className="block-title">{post.dish || post.restaurant}</div>
                      {post.coordinates ? <div className="coordinates">{post.coordinates}</div> : null}
                      <div className="TimeStamp">
                        <div className="TimeStampUpdated">Updated: {(post.updated_at || post.countries?.[0]?.updated_at)?.replace(/[TZ]/g, ' ')?.replace(/\.\d{3}/, '') || 'Date unavailable'}</div>
                      </div>
                    </div>

                    <HandleDuplicateFoodPosts post={post} toggleButtonRef={toggleButtonRef} openPanel={openPanel} setOpenPanel={setOpenPanel} />
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LatestPage;
